import { createFileRoute } from "@tanstack/react-router";
import type { BrewcontrolNodeId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { CalibrationPanel } from "@/features/brewcontrol/calibration/calibration-panel";
import {
  CalibrationHistoryTable,
  rowOfRecord,
} from "@/features/brewcontrol/calibration/calibration-history";
import { OriginName } from "@/features/brewcontrol/calibration/origin-name";
import { RoutineHistory } from "@/features/brewcontrol/calibration/routine-history";
import { NodeEventsStream } from "@/features/brewcontrol/use-node-events-stream";

export const Route = createFileRoute("/brewcontrol/$nodeId/calibration")({
  component: NodeCalibration,
});

/**
 * Calibration as the node sees it: one panel per vessel bound to a workspace
 * of this node, then every routine it ran and every calibration it recorded.
 */
function NodeCalibration() {
  const { nodeId } = Route.useParams();
  const { data: workspaces, isLoading } =
    trpc.brewcontrol.listWorkspaces.useQuery({ nodeId });
  const { data: runs } = trpc.brewcontrolRoutines.list.useQuery({ nodeId });
  const { data: catalog } = trpc.brewcontrolRoutines.catalog.useQuery({
    nodeId,
  });
  const { data: calibrations } =
    trpc.brewcontrolRoutines.listCalibrations.useQuery({ nodeId });

  const bound = (workspaces ?? []).filter((w) => w.equipment_id !== null);

  return (
    <div className="max-w-5xl space-y-8 p-4">
      <NodeEventsStream nodeId={nodeId} />

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : bound.length === 0 ? (
        <p className="text-sm text-olive-11">
          No vessel is bound to a workspace of this node; bind one in Config to
          calibrate.
        </p>
      ) : (
        bound.map((w) =>
          w.equipment_id === null ? null : (
            <CalibrationPanel key={w.id} equipmentId={w.equipment_id} />
          ),
        )
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Routine history</h3>
        {runs === undefined ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <RoutineHistory
            nodeId={nodeId}
            empty="This node never ran a routine."
            recordedRunIds={
              new Set(
                (calibrations ?? []).flatMap((c) =>
                  c.routine_run_id ? [c.routine_run_id] : [],
                ),
              )
            }
            rows={runs.map((run) => ({
              routineRunId: run.id,
              label: catalog?.[run.name]?.label ?? run.name,
              state: run.state,
              error: run.error,
              startedAt: run.started_at,
              finishedAt: run.finished_at,
              by: <OriginName origin={run.origin} />,
            }))}
          />
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Calibration records</h3>
        {calibrations === undefined ? (
          <Skeleton className="h-12 w-full" />
        ) : calibrations.length === 0 ? (
          <p className="text-sm text-olive-11">
            No calibration recorded on this node.
          </p>
        ) : (
          <CalibrationHistoryTable
            nodeId={nodeId as BrewcontrolNodeId}
            rows={calibrations.map(rowOfRecord)}
            showDevice
          />
        )}
      </section>
    </div>
  );
}
